import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api.js'
import { useAuth } from '../contexts/AuthContext.jsx'
import { CardHead, SettingsRow, Switch } from '../components/SettingsLayout.jsx'
import { PickerDropdown } from '../components/Dropdown.jsx'
import { useToast } from '../components/Toast.jsx'
import Icon from '../components/Icon.jsx'
import GlyphTile from '../components/GlyphTile.jsx'
import { TILE, modeTile } from '../lib/tileLook.js'
// The list the server validates against, not a copy of it — same rule as shared/scope.js.
import { PROVINCES, timezoneForProvince } from '../../../shared/provinces.js'

const CONFIGURE_TABS = [
  { key: 'modules', label: 'Modules' },
  { key: 'operations', label: 'Operations' },
]

/**
 * Settings → Configure: which parts of the product this company uses (Modules), plus the
 * handful of settings that apply no matter which of them is running (Operations) — time
 * zone, week start, and whether personal Microsoft accounts can sign in. Those used to live
 * on the Normal-mode setup page, which was misleading: they are not about that mode,
 * they're read by both.
 *
 * "Modules" rather than "Modes", because the two operating modes stopped being the only
 * things on that tab: the fleet band and Lists are switched on there too, and each is a
 * part of the product a company either uses or does not.
 */
export function Configure() {
  const [tab, setTab] = useState('modules')

  return (
    <>
      <div className="card">
        <CardHead
          title="Configure Tenant"
          sub={
            tab === 'modules'
              ? 'Turn the parts of the product your company uses on or off, and name them. You can always change this later.'
              : 'Settings that apply company-wide, no matter which mode is running.'
          }
          action={
            <div className="segmented">
              {CONFIGURE_TABS.map((t) => (
                <button key={t.key} className={tab === t.key ? 'selected' : ''} onClick={() => setTab(t.key)}>
                  {t.label}
                </button>
              ))}
            </div>
          }
        />
      </div>

      {tab === 'modules' ? <ModesPane /> : <OperationsPane />}
    </>
  )
}

/**
 * The stored identifiers never change — every assignment ever written says 'normal' or
 * 'cat', and renaming those to follow a caption would mean rewriting live scheduling data.
 * What a person reads is a label over the top, set here and used everywhere.
 *
 * Saved per field on blur rather than behind one Save button: this is four short fields
 * across two cards, and a save bar covering the pair would leave somebody wondering which
 * of the two it was about to write.
 */
function ModesPane() {
  const qc = useQueryClient()
  const toast = useToast()
  const { refresh } = useAuth()
  const { data } = useQuery({ queryKey: ['tenant-modes'], queryFn: () => api.get('/api/owner/modes') })
  const [busy, setBusy] = useState(null)
  // Which cards the person has opened. Every card starts shut: this page is read far more
  // often than it is edited — somebody comes to check which modes are on, and the switch and
  // the description already answer that — so opening all of them by default buried the
  // second card under the first one's form and made the page scroll for no one's benefit.
  const [openOverrides, setOpenOverrides] = useState({})

  if (!data) return <div className="card empty">Loading…</div>

  const isOpen = (m) => openOverrides[m.mode] ?? false
  const toggleOpen = (m) => setOpenOverrides((o) => ({ ...o, [m.mode]: !isOpen(m) }))

  async function patch(mode, body) {
    setBusy(mode)
    try {
      await api.put(`/api/owner/modes/${mode}`, body)
      qc.invalidateQueries({ queryKey: ['tenant-modes'] })
      // The vocabulary lives in the session payload, so every other screen picks the new
      // name up from the same refresh rather than waiting for a reload.
      refresh()
    } catch (err) {
      toast.error(err)
      qc.invalidateQueries({ queryKey: ['tenant-modes'] })
    } finally {
      setBusy(null)
    }
  }

  return (
    <>
      {data.modes.map((m) => {
        // The glyph and colours this mode wears everywhere — see lib/tileLook.js.
        const look = modeTile(m.mode)
        const open = isOpen(m)
        return (
          <div
            className={`card mode-config ${open ? 'open' : ''} ${m.enabled ? '' : 'off'}`}
            key={m.mode}
          >
            {/* Collapsed, the card is nothing but a disclosure: glyph, name, description,
                chevron. The switch used to sit beside them, which put the one irreversible
                control on the page a stray click away from somebody who only meant to open
                the card — and made the shut row a control panel rather than a heading. It
                now lives in the body, where reading it costs a deliberate click.

                A module that is off says so by looking off — the whole header greys, the
                glyph tile loses its colour and the card sits on a filled ground (see
                .mode-config.off) — rather than by wearing a tag that repeats it in words. */}
            <div className="mode-config-head">
              <button
                type="button"
                className="mode-config-toggle"
                aria-expanded={open}
                onClick={() => toggleOpen(m)}
              >
                <GlyphTile {...look} size={30} muted={!m.enabled} />
                <span className="mode-config-text">
                  {/* The tenant's own word for the mode, and then what it is: a module of
                      the product, named the same way as the two below it. */}
                  <span className="mode-config-name">{m.name} Module</span>
                  <span className="sub">{m.description}</span>
                </span>
                <Icon name={open ? 'up' : 'down'} size={17} />
              </button>
            </div>

            <div className="settings-list" hidden={!open}>
              <SettingsRow
                label="Enable this feature"
                hint="When off, nobody can switch the board onto it and its setup page is hidden."
              >
                <Switch checked={m.enabled} onChange={(enabled) => patch(m.mode, { enabled })} />
              </SettingsRow>

              <SettingsRow
                label="Name"
                hint="What this mode is called everywhere in the app. Leave blank for the default."
              >
                <input
                  type="text"
                  defaultValue={m.customName || ''}
                  placeholder={m.name}
                  disabled={busy === m.mode}
                  maxLength={40}
                  // On blur, not on every keystroke: a rename that fires per character would
                  // write forty rows and re-render every screen that shows the name.
                  onBlur={(e) => {
                    const next = e.target.value.trim()
                    if (next !== (m.customName || '')) patch(m.mode, { name: next })
                  }}
                />
              </SettingsRow>

              <SettingsRow label="Description" hint="Shown here, to explain the mode to whoever sets it up." stacked>
                <textarea
                  className="notify-note"
                  defaultValue={m.customDescription || ''}
                  placeholder={m.description}
                  disabled={busy === m.mode}
                  maxLength={280}
                  onBlur={(e) => {
                    const next = e.target.value.trim()
                    if (next !== (m.customDescription || '')) patch(m.mode, { description: next })
                  }}
                />
              </SettingsRow>
            </div>
          </div>
        )
      })}
      <AssetsCard />
      <ListsCard />
    </>
  )
}

/**
 * Assets on the board: whether the company's register is drawn under the crews and handed out
 * day by day, and what the company calls it.
 *
 * The register itself (Settings → Asset management) is always there and is not what this
 * switch controls. Stored as "asset" in the database whatever the caption says — the same
 * split the mode names above make between a stored identifier and a tenant's word. The fields
 * save on blur the way the mode fields do, and the three settings ride the same owner-gated
 * PUT as time zone and week start.
 */
function AssetsCard() {
  const qc = useQueryClient()
  const toast = useToast()
  const { refresh, assets } = useAuth()
  const { data } = useQuery({ queryKey: ['admin-settings'], queryFn: () => api.get('/api/admin/settings') })
  const [busy, setBusy] = useState(false)
  // Shut until asked for, like the mode cards above it.
  const [open, setOpen] = useState(false)

  if (!data) return null
  const onBoard = Boolean(data.assetsOnBoard)

  async function patch(body) {
    setBusy(true)
    try {
      await api.put('/api/admin/settings', body)
      qc.invalidateQueries({ queryKey: ['admin-settings'] })
      // The caption lives in the session payload, so the sidebar and the board pick the new
      // word up from the same refresh rather than waiting for a reload.
      refresh()
    } catch (err) {
      toast.error(err)
      qc.invalidateQueries({ queryKey: ['admin-settings'] })
    } finally {
      setBusy(false)
    }
  }

  const saveOnBlur = (key) => (e) => {
    const next = e.target.value.trim()
    if (next !== (data[key] || '')) patch({ [key]: next })
  }

  return (
    <div
      className={`card mode-config ${open ? 'open' : ''} ${onBoard ? '' : 'off'}`}
    >
      <div className="mode-config-head">
        <button
          type="button"
          className="mode-config-toggle"
          aria-expanded={open}
          onClick={() => setOpen(!open)}
        >
          <GlyphTile {...TILE.assets} size={30} muted={!onBoard} />
          <span className="mode-config-text">
            <span className="mode-config-name">Asset Module</span>
            <span className="sub">
              Track your assets within this app and assign them to team members.
            </span>
          </span>
          <Icon name={open ? 'up' : 'down'} size={17} />
        </button>
      </div>

      <div className="settings-list" hidden={!open}>
        <SettingsRow
          label="Enable this feature"
          hint="The register stays under Settings either way; this is only about the band."
        >
          <Switch checked={onBoard} onChange={(assetsOnBoard) => patch({ assetsOnBoard })} />
        </SettingsRow>

        <SettingsRow
          label="Name"
          hint='What the register is called everywhere in the app. Leave blank for "Assets".'
        >
          <input
            type="text"
            defaultValue={data.assetsName || ''}
            placeholder="Assets"
            disabled={busy}
            maxLength={40}
            onBlur={saveOnBlur('assetsName')}
          />
        </SettingsRow>

        <SettingsRow
          label="One item is called"
          hint='Used in buttons and menus: "Add vehicle", "Retire vehicle". Leave blank for "asset".'
        >
          <input
            type="text"
            defaultValue={data.assetsUnitName || ''}
            placeholder="asset"
            disabled={busy}
            maxLength={40}
            onBlur={saveOnBlur('assetsUnitName')}
          />
        </SettingsRow>
      </div>
    </div>
  )
}

/**
 * Lists: whether the company keeps named boards of people against days beside the schedule.
 * The lists themselves are set up under Settings → Lists once this is on; the switch is all
 * that lives here, in the same card the fleet band and the modes above it use.
 */
function ListsCard() {
  const qc = useQueryClient()
  const toast = useToast()
  const { refresh } = useAuth()
  const { data } = useQuery({ queryKey: ['admin-settings'], queryFn: () => api.get('/api/admin/settings') })
  const [open, setOpen] = useState(false)

  if (!data) return null
  const on = Boolean(data.listsEnabled)

  async function patch(body) {
    try {
      await api.put('/api/admin/settings', body)
      qc.invalidateQueries({ queryKey: ['admin-settings'] })
      // The flag lives in the session payload, so the nav and the sidebar pick it up from
      // the same refresh rather than waiting for a reload.
      refresh()
    } catch (err) {
      toast.error(err)
      qc.invalidateQueries({ queryKey: ['admin-settings'] })
    }
  }

  return (
    <div className={`card mode-config ${open ? 'open' : ''} ${on ? '' : 'off'}`}>
      <div className="mode-config-head">
        <button
          type="button"
          className="mode-config-toggle"
          aria-expanded={open}
          onClick={() => setOpen(!open)}
        >
          <GlyphTile {...TILE.lists} size={30} muted={!on} />
          <span className="mode-config-text">
            <span className="mode-config-name">Lists Module</span>
            <span className="sub">Create and Manage extra Scheduling Boards</span>
          </span>
          <Icon name={open ? 'up' : 'down'} size={17} />
        </button>
      </div>

      <div className="settings-list" hidden={!open}>
        <SettingsRow
          label="Enable this feature"
          hint="Adds Lists to the navigation, and a Lists page under Settings where an owner sets them up."
        >
          <Switch checked={on} onChange={(listsEnabled) => patch({ listsEnabled })} />
        </SettingsRow>
      </div>
    </div>
  )
}

const WEEK_START = [[1, 'Monday'], [7, 'Sunday'], [6, 'Saturday']]

/**
 * "Pacific Time", "Central Standard Time" — the name a person knows a zone by. The generic
 * form, not a city: a company in Quebec should not read "Toronto", nor one in Yellowknife
 * "Edmonton", though those are the zones they keep. Falls back to the raw name for a zone
 * this browser cannot name.
 */
function zoneName(tz) {
  try {
    return (
      new Intl.DateTimeFormat('en-CA', { timeZone: tz, timeZoneName: 'longGeneric' })
        .formatToParts(Date.now())
        .find((part) => part.type === 'timeZoneName')?.value || tz
    )
  } catch {
    return tz
  }
}

/**
 * Province decides the time zone, and the time zone decides which calendar day "today" is —
 * everything from the mode switch boundary to the schedule email reads off it. Week start
 * only reorders the columns the week view and printouts show. Allow personal Microsoft
 * accounts controls sign-in: with it off, an invited person whose Microsoft account is
 * personal (not work/school) is refused at login even though their invite is valid — see
 * server/src/routes/auth.js.
 */
function OperationsPane() {
  const qc = useQueryClient()
  const toast = useToast()
  const { data } = useQuery({ queryKey: ['admin-settings'], queryFn: () => api.get('/api/admin/settings') })
  const [form, setForm] = useState(null)
  const v = form || data

  if (!v) return <div className="card empty">Loading…</div>

  const set = (patch) => { setForm({ ...v, ...patch }) }

  // Sent only when it moved. The zone follows it on the server, and a company whose zone
  // deliberately differs from its province's must not have it reset by saving the week start.
  const provinceMoved = (v.province ?? null) !== (data.province ?? null)
  // The zone this company's days will be counted in once saved: the province's, if that is
  // what is changing, otherwise whatever is stored — which is the true answer for a company
  // saved with a zone of its own.
  const zone = provinceMoved ? timezoneForProvince(v.province) || v.timezone : v.timezone

  async function save(e) {
    e.preventDefault()
    try {
      await api.put('/api/admin/settings', {
        ...(provinceMoved ? { province: v.province } : {}),
        weekStart: Number(v.weekStart),
        allowPersonalMs: v.allowPersonalMs,
      })
      toast.success('Saved.')
      qc.invalidateQueries({ queryKey: ['admin-settings'] })
      qc.invalidateQueries({ queryKey: ['me'] })
      setForm(null)
    } catch (err) {
      toast.error(err)
    }
  }

  return (
    <>
      <form className="card" id="operations-settings" onSubmit={save}>
        <div className="settings-list">
          <SettingsRow
            label="Province"
            hint={`Sets the time zone schedule days are counted in (${zoneName(zone)}) and the statutory holidays marked on the board.`}
          >
            {/* The one setting where there used to be two: a time zone picker beside this
                offered one sensible answer per province and could be set to disagree with
                it. The zone is shown in the hint so what "today" means is never hidden. */}
            <PickerDropdown
              value={v.province ?? null}
              onPick={(province) => set({ province })}
              placeholder="Choose your province"
              searchable
              options={PROVINCES.map(([id, label]) => ({ value: id, label }))}
            />
          </SettingsRow>

          <SettingsRow label="Week starts on">
            <PickerDropdown
              value={v.weekStart}
              onPick={(weekStart) => set({ weekStart })}
              options={WEEK_START.map(([n, l]) => ({ value: n, label: l }))}
            />
          </SettingsRow>

          <SettingsRow
            label="Allow personal Microsoft accounts"
            hint="Invited people only, either way — this never opens sign-up."
          >
            <Switch checked={v.allowPersonalMs} onChange={(c) => set({ allowPersonalMs: c })} />
          </SettingsRow>
        </div>
      </form>

      <div className="settings-actions">
        {form && <span className="actions-note">Unsaved changes</span>}
        <button className="btn primary" type="submit" form="operations-settings" disabled={!form}>
          Save changes
        </button>
      </div>
    </>
  )
}
